<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue'
import type {
  NavigationItemsProps,
  NavigationItem,
} from '../../models/navigation/NavigationItems.model'
import { useLocalePath } from '#i18n'
import { useRoute } from '#imports'
import type { ComponentPublicInstance } from 'vue'
import AppNavigationItem from './AppNavigationItem.vue'
import AppNavigationSubmenuMobile from './AppNavigationSubmenuMobile.vue'
import AppNavigationSubmenuDesktop from './AppNavigationSubmenuDesktop.vue'

interface FocusableComponent extends ComponentPublicInstance {
  focusFirstItem: () => void
}

const props = defineProps<NavigationItemsProps>()
const emit = defineEmits<{
  click: []
  navigate: [item: NavigationItem]
}>()

const route = useRoute()
const activeSubmenu = ref<NavigationItem | null>(null)
const tooltipPosition = ref({ top: 0, left: 0 })
const expandedMobileItems = ref<Set<string>>(new Set())
const pointerYOffset = ref(0)
const expandedSubmenuContentTop = ref(0)
const navItemsListRef = ref<HTMLElement | null>(null)
const desktopSubmenuRef = ref<FocusableComponent | null>(null)
const localePath = useLocalePath()
const isKeyboardNavigation = ref(false)

const isMobile = computed(
  () => typeof window !== 'undefined' && window.innerWidth < 1024
)

const isItemActive = (item: NavigationItem): boolean => {
  if (!route?.path || !item.to) return false

  const itemPath = localePath(item.to)
  return (
    route.path === itemPath ||
    !!item.children?.some(
      (child) => child.to && route.path === localePath(child.to)
    )
  )
}

const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as HTMLElement
  const isClickInsideNavItem =
    target.closest('[data-testid="nav-item"]') !== null
  const isClickInsideSubmenu =
    target.closest('[data-testid="mobile-submenu"]') !== null ||
    target.closest('[data-testid="submenu-collapsed-flyout"]') !== null ||
    target.closest('[data-testid="submenu-expanded-slideover"]') !== null ||
    target.closest('[data-testid="mobile-subitem-link"]') !== null

  if (!isClickInsideNavItem && !isClickInsideSubmenu) {
    activeSubmenu.value = null
    expandedMobileItems.value.clear()
  }
}

const handleGlobalKeyDown = (event: KeyboardEvent) => {
  isKeyboardNavigation.value = true
  if (event.key === 'Escape') {
    handleCloseSubmenu()
  }
}

const isItemExpanded = (item: NavigationItem): boolean => {
  return expandedMobileItems.value.has(item.label)
}

const openSubmenu = (item: NavigationItem, targetElement: HTMLElement) => {
  if (!item.children) return

  activeSubmenu.value = item
  if (isMobile.value) {
    expandedMobileItems.value.add(item.label)
  } else {
    const rect = targetElement.getBoundingClientRect()
    if (props.isCollapsed) {
      tooltipPosition.value = {
        top: rect.top - 30,
        left: rect.right + 4,
      }
      pointerYOffset.value = rect.top + rect.height / 2 - (rect.top - 30)
    } else {
      const firstItemEl = navItemsListRef.value
        ?.firstElementChild as HTMLElement
      expandedSubmenuContentTop.value =
        firstItemEl?.getBoundingClientRect().top ?? 0
    }
  }

  if (isKeyboardNavigation.value) {
    nextTick(() => {
      desktopSubmenuRef.value?.focusFirstItem()
    })
  }
}

const handleItemClick = (item: NavigationItem, event: MouseEvent) => {
  if (item.to && !item.children) {
    activeSubmenu.value = null
    expandedMobileItems.value.clear()
    emit('navigate', item)
    return
  }

  if (item.children) {
    event.preventDefault()
    if (activeSubmenu.value?.label === item.label) {
      activeSubmenu.value = null
    } else {
      openSubmenu(item, event.currentTarget as HTMLElement)
    }
  }
}

const handleCloseSubmenu = () => {
  const previouslyFocusedItem = document.activeElement as HTMLElement
  activeSubmenu.value = null
  expandedMobileItems.value.clear()
  if (isKeyboardNavigation.value) {
    nextTick(() => {
      previouslyFocusedItem?.focus()
    })
  }
}

const handleResize = () => {
  if (isMobile.value) {
    activeSubmenu.value = null
  } else {
    expandedMobileItems.value.clear()
  }
}

const handleMobileNavigate = (item: NavigationItem) => {
  activeSubmenu.value = null
  expandedMobileItems.value.clear()
  emit('navigate', item)
}

onMounted(() => {
  window.addEventListener('resize', handleResize)
  handleResize()
  document.addEventListener('keydown', handleGlobalKeyDown)
  document.addEventListener('click', handleClickOutside)
  window.addEventListener('mousedown', () => {
    isKeyboardNavigation.value = false
  })
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize)
  document.removeEventListener('keydown', handleGlobalKeyDown)
  document.removeEventListener('click', handleClickOutside)
  window.removeEventListener('mousedown', () => {
    isKeyboardNavigation.value = false
  })
})
</script>

<template>
  <div class="relative flex h-full flex-col overflow-hidden">
    <!-- Main Navigation Panel -->
    <nav
      v-show="!(isMobile && expandedMobileItems.size > 0)"
      :class="[
        'w-full flex-grow overflow-y-auto py-5 transition-all duration-300',
        props.isCollapsed ? 'px-3' : 'px-5',
      ]"
      aria-label="Main Navigation"
    >
      <div ref="navItemsListRef" role="menu" aria-orientation="vertical">
        <AppNavigationItem
          v-for="item in props.items"
          :key="item.label"
          :data-item-label="item.label"
          :item="item"
          :is-collapsed="props.isCollapsed"
          :is-active="isItemActive(item)"
          :is-expanded="
            activeSubmenu?.label === item.label || isItemExpanded(item)
          "
          :is-mobile="isMobile"
          @click-item="handleItemClick"
        />
      </div>
    </nav>

    <!-- Mobile Submenu Panel (teleported to body for mobile only) -->
    <Teleport v-if="isMobile" to="body">
      <AppNavigationSubmenuMobile
        v-if="expandedMobileItems.size > 0 && activeSubmenu"
        :item="activeSubmenu"
        :is-item-active="isItemActive"
        @close="handleCloseSubmenu"
        @navigate="handleMobileNavigate"
      />
    </Teleport>

    <!-- Desktop Submenu (rendered in place for desktop) -->
    <AppNavigationSubmenuDesktop
      v-if="!isMobile && activeSubmenu"
      ref="desktopSubmenuRef"
      :item="activeSubmenu"
      :is-collapsed="props.isCollapsed"
      :is-item-active="isItemActive"
      :tooltip-position="tooltipPosition"
      :pointer-y-offset="pointerYOffset"
      :expanded-submenu-content-top="expandedSubmenuContentTop"
      :is-keyboard-navigation="isKeyboardNavigation"
      @close-submenu="handleCloseSubmenu"
      @key-nav="() => {}"
      @focus-main-nav-item="() => {}"
      @navigate="emit('click')"
    />
  </div>
</template>
